import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { infaqService, DonationCampaign } from '../../../services/infaqService';
import LoadingSpinner from '../../../components/LoadingSpinner';

function InfaqCampaignFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [campaign, setCampaign] = useState<DonationCampaign | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Same restrictions the server enforces on POST /infaq/campaigns/image.
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Hanya fail JPEG, PNG, WebP atau GIF dibenarkan');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setError('Saiz imej mesti kurang daripada 5 MB');
      e.target.value = '';
      return;
    }
    setError(null);
    setUploadingImage(true);
    try {
      const { url } = await infaqService.uploadCampaignImage(file);
      setFormData((prev) => ({ ...prev, featuredImage: url }));
    } catch (err: any) {
      setError(err.message || 'Gagal memuat naik imej');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    featuredImage: '',
    targetAmount: 10000,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'ACTIVE',
  });

  useEffect(() => {
    if (isEdit && id) {
      fetchCampaign(id);
    }
    window.scrollTo(0, 0);
  }, [id]);

  const fetchCampaign = async (campaignId: string) => {
    try {
      const campaigns = await infaqService.getAllCampaigns();
      const found = campaigns.find((c) => c.id === campaignId);
      if (found) {
        setCampaign(found);
        setFormData({
          title: found.title,
          slug: found.slug,
          description: found.description,
          featuredImage: found.featuredImage || '',
          targetAmount: found.targetAmount,
          startDate: new Date(found.startDate).toISOString().split('T')[0],
          endDate: found.endDate ? new Date(found.endDate).toISOString().split('T')[0] : '',
          status: found.status,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Gagal memuat kempen');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      if (isEdit && id) {
        await infaqService.updateCampaign(id, formData);
      } else {
        // Create campaign - need to implement this endpoint or use the existing one
        await infaqService.createCampaign(formData as any);
      }

      navigate('/admin/infaq/campaigns');
    } catch (err: any) {
      alert(err.message || 'Gagal menyimpan kempen');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin/infaq/campaigns" className="inline-flex items-center gap-2 text-[#0D7330] hover:underline mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali ke Senarai Kempen
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Kemaskini Kempen' : 'Kempen Baru'}
          </h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tajuk Kempen *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    title: e.target.value,
                    slug: isEdit ? formData.slug : generateSlug(e.target.value),
                  });
                }}
                placeholder="Contoh: Bantuan Mangsa Banjir"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                required
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug (URL) *
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="bantuan-mangsa-banjir"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Slug akan digunakan dalam URL kempen</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Penerangan *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Terangkan tujuan kempen ini..."
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                required
              />
            </div>

            {/* Featured Image — paste a link or upload a file */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Imej Utama
              </label>
              <input
                type="url"
                value={formData.featuredImage}
                onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tampal pautan imej, atau muat naik fail di bawah (JPEG/PNG/WebP/GIF, maksimum 5 MB).
              </p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="mt-2 block w-full text-sm text-gray-500 border border-gray-300 rounded-lg px-3 py-2 file:mr-3 file:rounded-md file:border-0 file:bg-[#0D7330] file:px-3 file:py-1.5 file:text-white file:text-xs hover:file:bg-[#0a5c26] disabled:opacity-60"
              />
              {uploadingImage && (
                <p className="text-xs text-gray-500 mt-1">Memuat naik imej...</p>
              )}
              {formData.featuredImage && !uploadingImage && (
                <img
                  key={formData.featuredImage}
                  src={formData.featuredImage}
                  alt="Pratonton imej utama"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                  className="mt-3 max-h-40 rounded-lg border border-gray-200 object-cover"
                />
              )}
            </div>

            {/* Target Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sasaran Kutipan (RM) *
              </label>
              <input
                type="number"
                value={formData.targetAmount}
                onChange={(e) => setFormData({ ...formData, targetAmount: parseFloat(e.target.value) })}
                min="1"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                required
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tarikh Mula *
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                required
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tarikh Tamat
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                min={formData.startDate}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Biarkan kosong jika tiada tarikh tamat</p>
            </div>

            {/* Status */}
            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                >
                  <option value="ACTIVE">Aktif</option>
                  <option value="PAUSED">Dijedakan</option>
                  <option value="COMPLETED">Selesai</option>
                  <option value="CANCELLED">Dibatalkan</option>
                </select>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 mt-8 pt-8 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 bg-[#0D7330] text-white font-semibold rounded-lg hover:bg-[#0a5c26] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Menyimpan...' : isEdit ? 'Kemaskini' : 'Cipta'}
            </button>
            <Link
              to="/admin/infaq/campaigns"
              className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Batal
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default InfaqCampaignFormPage;
